import type { GeometryElement } from 'jsxgraph';
import type { JsxgraphThemeService } from '../../jsxgraph-theme.service';
import { computeArrowRoute } from '../../util/arrow-routing';
import type { ElementCreatorContext } from '../core/board.types';
import { parseThemeColor, resolveColor } from '../core/color.utils';
import { deepMerge } from '../core/deepMerge';
import { bindLabel } from '../label/label.bind';
import type { ResolvedLabelConfig } from '../label/label.model';
import type { ElementTheme, LineStyle } from '../theme/theme.model';
import type { ArrowAnnotationArgs, ArrowAnnotationResult } from './arrow-annotation.model';

/** Default line style for arrow annotations. */
const DEFAULT_ARROW_LINE: Required<Pick<LineStyle, 'color' | 'strokeWidth' | 'arrows'>> = {
  color: 'neutral',
  strokeWidth: 1.5,
  arrows: { last: true, type: 2, size: 6 },
};

/** Extra clearance (in pixels) between the label edge and the arrow start. */
const LABEL_ARROW_GAP_PX = 4;

/**
 * Wrap a static value as a getter function; pass through existing functions.
 */
function coordGetter(v: number | (() => number)): () => number {
  return typeof v === 'function' ? v : () => v;
}

/**
 * Evaluate a coordinate component to a static number.
 */
function evalCoord(v: number | (() => number)): number {
  return typeof v === 'function' ? v() : v;
}

/**
 * Compute the distance from the center of an axis-aligned rectangle to its edge
 * along a given direction. Used to find where an arrow should start so it clears
 * the label text bounding box.
 *
 * The intersection is computed in pixel space (since the label bounding box is
 * measured in pixels), then converted back to math-coordinate distance.
 *
 * @param halfW - Half-width of the rectangle in pixels
 * @param halfH - Half-height of the rectangle in pixels
 * @param dx - Direction x component (math coords)
 * @param dy - Direction y component (math coords)
 * @param pxPerCoordX - Pixels per math-coordinate unit along x
 * @param pxPerCoordY - Pixels per math-coordinate unit along y
 * @returns Distance in math coordinates from center to rectangle edge along [dx, dy], or 0 if degenerate
 */
function rayBoxClearance(
  halfW: number,
  halfH: number,
  dx: number,
  dy: number,
  pxPerCoordX: number,
  pxPerCoordY: number,
): number {
  // Convert direction to pixel-space for the intersection test
  const pxDx = dx * pxPerCoordX;
  const pxDy = dy * pxPerCoordY;
  const len = Math.sqrt(pxDx * pxDx + pxDy * pxDy);
  if (len < 1e-9) {
    return 0;
  }

  // Normalized direction in pixel space
  const nx = pxDx / len;
  const ny = pxDy / len;

  // Ray from center along (nx, ny): find t where ray exits the box [-halfW, halfW] x [-halfH, halfH]
  const tx = Math.abs(nx) > 1e-9 ? halfW / Math.abs(nx) : Infinity;
  const ty = Math.abs(ny) > 1e-9 ? halfH / Math.abs(ny) : Infinity;
  const tPx = Math.min(tx, ty) + LABEL_ARROW_GAP_PX;

  // Convert pixel-space exit point back to math-coordinate distance
  const exitCoordX = (nx * tPx) / pxPerCoordX;
  const exitCoordY = (ny * tPx) / pxPerCoordY;
  return Math.sqrt(exitCoordX * exitCoordX + exitCoordY * exitCoordY);
}

/**
 * Create an arrow annotation — a label connected to a target point by a routed arrow path.
 *
 * @param ctx - Element creator context (board, container)
 * @param themeService - Theme service for color resolution
 * @param theme - Element theme with defaults
 * @param args - Arrow annotation arguments
 * @returns Arrow annotation result with objects array and optional label
 */
export function createArrowAnnotation(
  ctx: ElementCreatorContext,
  themeService: JsxgraphThemeService,
  theme: ElementTheme,
  args: ArrowAnnotationArgs,
): ArrowAnnotationResult {
  const { board, container } = ctx;
  const { from, to, text, routing = 'orthogonal', style } = args;

  // ── 1. Resolve line style ──────────────────────────────────────────────
  const arrowTheme = theme.arrowAnnotation;
  const themeLineDefaults = arrowTheme.line ?? DEFAULT_ARROW_LINE;
  const mergedLine = deepMerge(themeLineDefaults, style?.line) as Required<
    Pick<LineStyle, 'color' | 'strokeWidth' | 'arrows'>
  > &
    LineStyle;

  const strokeColor = resolveColor(themeService, mergedLine.color ?? DEFAULT_ARROW_LINE.color) ?? 'currentColor';
  const strokeWidth = mergedLine.strokeWidth ?? DEFAULT_ARROW_LINE.strokeWidth;
  const dash = mergedLine.dash ?? 0;
  const visible = style?.visible ?? mergedLine.visible ?? true;
  const arrows = mergedLine.arrows ?? DEFAULT_ARROW_LINE.arrows;

  // ── 2. Create hidden anchor points ─────────────────────────────────────
  const fromX = coordGetter(from[0] as number | (() => number));
  const fromY = coordGetter(from[1] as number | (() => number));
  const toX = coordGetter(to[0] as number | (() => number));
  const toY = coordGetter(to[1] as number | (() => number));

  const labelAnchor = board.create('point', [fromX, fromY], {
    visible: false,
    fixed: true,
    size: 0,
    withLabel: false,
    highlight: false,
    showInfobox: false,
  }) as GeometryElement;

  const targetAnchor = board.create('point', [toX, toY], {
    visible: false,
    fixed: true,
    size: 0,
    withLabel: false,
    highlight: false,
    showInfobox: false,
  }) as GeometryElement;

  const objects: GeometryElement[] = [labelAnchor, targetAnchor];

  // ── 3. Base line attributes ────────────────────────────────────────────
  const baseLineAttrs = {
    strokeColor,
    strokeWidth,
    dash,
    visible,
    highlight: false,
    withLabel: false,
    straightFirst: false,
    straightLast: false,
    firstArrow: false,
    lastArrow: false,
  };

  // Arrow head config for the final segment (false = no arrow)
  const showLastArrow = arrows.last !== false;
  const arrowHead = showLastArrow
    ? {
        type: (typeof arrows.last === 'object' ? arrows.last.type : arrows.type) ?? 2,
        size: (typeof arrows.last === 'object' ? arrows.last.size : arrows.size) ?? 6,
      }
    : false;

  // ── 4. Compute waypoints & extract elbow coordinate helper ─────────────
  const waypoints = computeArrowRoute(
    [evalCoord(from[0]), evalCoord(from[1])],
    [evalCoord(to[0]), evalCoord(to[1])],
    routing,
  );

  // Elbow coordinate helper: shared between gap point and elbow point creation
  const elbowCoords = (): [number, number] => {
    const sx = evalCoord(from[0]);
    const sy = evalCoord(from[1]);
    const tx = evalCoord(to[0]);
    const ty = evalCoord(to[1]);
    const adx = Math.abs(tx - sx);
    const ady = Math.abs(ty - sy);

    if (routing === 'horizontal-first' || (routing === 'orthogonal' && adx >= ady)) {
      return [tx, sy];
    }
    return [sx, ty];
  };

  // ── 5. Create label (before line segments so gap point can reference it) ─
  const labelColor = parseThemeColor(style?.label?.color ?? mergedLine.color ?? DEFAULT_ARROW_LINE.color).name;

  const labelConfig: ResolvedLabelConfig = {
    visible: style?.label?.visible ?? true,
    variant: style?.label?.variant ?? arrowTheme.label?.variant ?? 'secondary',
    color: labelColor,
    position: style?.label?.position ?? 'center',
    positionIsExplicit: style?.label?.position !== undefined,
    contentVariant: 'name',
    text,
    offset: style?.label?.offset ?? [0, 0],
    popup: style?.label?.popup,
    fontSize: style?.label?.fontSize,
    textAlign: style?.label?.textAlign,
    spacing: style?.label?.spacing ?? 'normal',
  };

  const result: ArrowAnnotationResult = { objects };

  result.label = bindLabel({
    ctx,
    themeService,
    element: labelAnchor,
    config: labelConfig,
  });

  // ── 6. Create reactive arrow-start gap point ────────────────────────────
  // The arrow must not start at labelAnchor because that overlaps the label.
  // Create an invisible point offset along the direction to the next waypoint
  // by the label's half-bounding-box size plus gap padding so the arrow starts
  // at the label edge.
  const label = result.label;

  const computeGapOffset = (): [number, number] => {
    if (!label) {
      return [0, 0];
    }

    const rendNode = (label as unknown as { rendNode: HTMLElement }).rendNode;
    if (!rendNode) {
      return [0, 0];
    }

    const rect = rendNode.getBoundingClientRect();
    if (rect.width === 0 || rect.height === 0) {
      return [0, 0];
    }

    const halfW = rect.width / 2;
    const halfH = rect.height / 2;

    // Direction from label anchor toward the first waypoint after it
    const sx = evalCoord(from[0]);
    const sy = evalCoord(from[1]);
    const isElbow = waypoints.length > 2;
    const nextX = isElbow ? elbowCoords()[0] : evalCoord(to[0]);
    const nextY = isElbow ? elbowCoords()[1] : evalCoord(to[1]);
    const dx = nextX - sx;
    const dy = nextY - sy;
    const dirLen = Math.sqrt(dx * dx + dy * dy);

    if (dirLen < 1e-9) {
      return [0, 0];
    }

    // Pixels per math-coordinate unit
    const bbox = board.getBoundingBox();
    const cRect = container.getBoundingClientRect();
    const pxPerCoordX = cRect.width / (bbox[2] - bbox[0]);
    const pxPerCoordY = cRect.height / (bbox[1] - bbox[3]);

    const clearance = rayBoxClearance(halfW, halfH, dx, dy, pxPerCoordX, pxPerCoordY);
    if (clearance < 1e-9) {
      return [0, 0];
    }

    // Clamp clearance to 80% of distance to avoid overshooting
    const clampedClearance = Math.min(clearance, dirLen * 0.8);
    return [(dx / dirLen) * clampedClearance, (dy / dirLen) * clampedClearance];
  };

  // Cached gap offset: computed in X getter, reused in Y getter (JSXGraph evaluates X first)
  let cachedGapOffset: [number, number] = [0, 0];

  const arrowStart = board.create(
    'point',
    [
      () => {
        cachedGapOffset = computeGapOffset();
        return fromX() + cachedGapOffset[0];
      },
      () => fromY() + cachedGapOffset[1],
    ],
    {
      visible: false,
      fixed: true,
      size: 0,
      withLabel: false,
      highlight: false,
      showInfobox: false,
    },
  ) as GeometryElement;
  objects.push(arrowStart);

  // ── 7. Create line segments ────────────────────────────────────────────
  if (waypoints.length === 2) {
    // Straight line: single segment with arrow
    const line = board.create('line', [arrowStart, targetAnchor], {
      ...baseLineAttrs,
      ...(arrowHead ? { lastArrow: { ...arrowHead } } : {}),
    }) as GeometryElement;
    objects.push(line);
  } else {
    // Elbow (3 waypoints): create reactive elbow point + two segments
    const elbowPoint = board.create('point', [() => elbowCoords()[0], () => elbowCoords()[1]], {
      visible: false,
      fixed: true,
      size: 0,
      withLabel: false,
      highlight: false,
      showInfobox: false,
    }) as GeometryElement;
    objects.push(elbowPoint);

    // First segment: arrow start (gap point or label anchor) → elbow (no arrow)
    const seg1 = board.create('line', [arrowStart, elbowPoint], {
      ...baseLineAttrs,
    }) as GeometryElement;
    objects.push(seg1);

    // Second segment: elbow → target anchor (with arrow)
    const seg2 = board.create('line', [elbowPoint, targetAnchor], {
      ...baseLineAttrs,
      ...(arrowHead ? { lastArrow: { ...arrowHead } } : {}),
    }) as GeometryElement;
    objects.push(seg2);
  }

  // Bootstrap gap point: after the first paint the label DOM node has valid
  // dimensions, so a board update positions the arrow start correctly.
  requestAnimationFrame(() => board.update());

  return result;
}
